import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "../components/functions/GlobalContext"
import {
    Box,
    Text,
    VStack,
    HStack,
    Center,
    SimpleGrid,
    Image,
    Spinner,
    Badge,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Icon,
    Tooltip,
} from "@chakra-ui/react"
import InfoModal from "../components/modals/InfoModal"
import ConfirmModal from "../components/modals/ConfirmModal"
import RantingInfoModal from "../components/modals/RantingInfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "../components/functions/Twirp"
import { fixStringLength } from "../components/functions/Common"
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios'
import { AiOutlineCheckCircle } from 'react-icons/ai'
import { MdOutlineDoNotDisturbAlt } from 'react-icons/md'

type RouteParams = {
    userid: string
}

type PesilatData = {
    id: number
    nama: string
    username: string
    country_id: number
    kelamin: string
    pendidikan: number
    sabuk: number
    tahun_masuk: number
    athlete: boolean
    coach: boolean
    coach_level: number
    referee: boolean
    referee_level: number
    referee_first_upgrade: number
    referee_last_upgrade: number
    other_profession: string
    file1: string
}

export default function ProfileViewPage() {
    const { t } = useTranslation();
    const globalContext = useContext(GlobalContextProvider)
    const navigate = useNavigate()
    const params = useParams<RouteParams>();

    const [pesilat, setPesilat] = useState<PesilatData | null>(null)
    const [countryName, setCountryName] = useState("")
    const [loading, setLoading] = useState(true)

    const [modalData, setModalData] = useState({
        title: "",
        main: "",
        isOpen: false,
    })
    const closeModal = () => {
        setModalData({ ...modalData, isOpen: false })
    }

    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const isAdmin = globalContext?.GetLoginData().isAdmin || false

    const [athletePrestasi, setAthletePrestasi] = useState<{ year: string, level: number, event: string, medal: number }[]>([])
    const [coachExp, setCoachExp] = useState<{ year: string, position: number, team: string }[]>([])
    const [rantingList, setRantingList] = useState<{ year: string, position: number, active: boolean, ranting: number }[]>([])
    const [infoRantingModalOpen, setInfoRantingModalOpen] = useState(false)
    const [rantingToView, setRantingToView] = useState(0)

    const prestasiLevelList = ["-", t("Ranting"), t("City"), t("Province"), t("National"), t("International")]
    const medalList = ["-", t("Bronze"), t("Silver"), t("Gold"), t("Other")]
    const posisiList = ["-", t("member"), t("Coach"), t("Supervisor")]

    const deleteUser = async () => {
        if (!pesilat) {
            return
        }
        setConfirmDelete(false)
        setDeleting(true)
        try {
            await axios.delete(Const.API_DeleteProfile + pesilat.id, {
                timeout: 15000,
                headers: { Authorization: globalContext?.GetLoginData().token },
            })
            setModalData({ ...modalData, isOpen: true, title: t("Successful"), main: t("user_deleted") })
            setPesilat(null)
        } catch (err) {
            let errorToPrint = (err as Error).message
            if (axios.isAxiosError(err)) {
                const error = err as AxiosError
                if (error.response?.hasOwnProperty("data")) {
                    errorToPrint = error.response.data as string
                }
                if (error.response?.status == 401) {
                    navigate('/logout')
                    return
                }
            }
            setModalData({ ...modalData, isOpen: true, title: t("Failed"), main: errorToPrint })
        }
        setDeleting(false)
    }

    const pendidikanList = [
        { value: 0, label: "-" },
        { value: 1, label: t("Pre-school") }, { value: 2, label: t("Elementary School") },
        { value: 3, label: t("High School") }, { value: 4, label: t("Diploma") },
        { value: 5, label: t("Under Graduate") }, { value: 6, label: t("Master") },
        { value: 7, label: t("Doctoral") },
    ]
    const sabukList = [
        { value: 0, label: "-" },
        { value: 1, label: t("Black") }, { value: 2, label: t("Blue") },
        { value: 3, label: t("Blue Belati 1") }, { value: 4, label: t("Blue Belati 2") },
        { value: 5, label: t("Green") }, { value: 6, label: t("Green Belati 1") },
        { value: 7, label: t("Green Belati 2") }, { value: 8, label: t("Brown") },
        { value: 9, label: t("Brown Belati 1") }, { value: 10, label: t("Brown Belati 2") },
        { value: 11, label: t("Purple") }, { value: 12, label: t("Red") },
        { value: 13, label: t("Orange") }, { value: 14, label: t("Yellow") },
        { value: 15, label: t("White") },
    ]
    const levelList = [
        { value: 0, label: "-" },
        { value: 1, label: t("Ranting") },
        { value: 2, label: t("City 1") }, { value: 3, label: t("City 2") }, { value: 4, label: t("City 3") },
        { value: 5, label: t("Province 1") }, { value: 6, label: t("Province 2") }, { value: 7, label: t("Province 3") },
        { value: 8, label: t("National 1") }, { value: 9, label: t("National 2") }, { value: 10, label: t("National 3") },
        { value: 11, label: t("International 1") }, { value: 12, label: t("International 2") }, { value: 13, label: t("International 3") },
    ]

    const loadLocationInfo = async (idToSearch: number) => {
        const tprops: TwirpProps = {
            url: Const.API_ReadCountry, headers: {}, timeout: 5000,
            data: { id: idToSearch }
        }
        const result = await Twirp(tprops)
        if (result.state == 0) {
            setCountryName(result.data.nama)
        }
    }

    const loadPesilat = async (idToSearch: number) => {
        setLoading(true)
        const tprops: TwirpProps = {
            url: Const.API_ReadPesilat,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 5000,
            data: { id: idToSearch }
        }
        const result = await Twirp(tprops)
        if (result.state == 2) {
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else {
            const data = result.data
            setPesilat({
                id: data.id,
                nama: data.nama,
                username: data.username,
                country_id: data.country_id,
                kelamin: data.kelamin,
                pendidikan: data.pendidikan,
                sabuk: data.sabuk,
                tahun_masuk: data.tahun_masuk,
                athlete: data.athlete,
                coach: data.coach,
                coach_level: data.coach_level,
                referee: data.referee,
                referee_level: data.referee_level,
                referee_first_upgrade: data.referee_first_upgrade,
                referee_last_upgrade: data.referee_last_upgrade,
                other_profession: data.other_profession,
                file1: data.file1,
            })
            if (data.country_id > 0) {
                loadLocationInfo(data.country_id)
            }
        }
        setLoading(false)
    }

    const loadPrestasi = async (idToSearch: number, type: number) => {
        const tprops: TwirpProps = {
            url: Const.API_ReadPrestasi,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 5000,
            data: { pesilat_id: idToSearch, type: type }
        }
        const result = await Twirp(tprops)
        if (result.state == 1 && result.axiosError.response?.status == 401) {
            navigate('/logout')
            return
        }
        if (result.state == 0) {
            const list = result.data.prestasiList || []
            if (type == 1) {
                setAthletePrestasi(list.map((v: any) => ({ year: "" + v.year, level: +v.level, event: v.event, medal: +v.medal })))
            } else {
                setCoachExp(list.map((v: any) => ({ year: "" + v.year, position: +v.position, team: v.team })))
            }
        }
    }

    const loadRantingList = async (idToSearch: number) => {
        const tprops: TwirpProps = {
            url: Const.API_ListRantingPesilat,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 5000,
            data: { id: idToSearch }
        }
        const result = await Twirp(tprops)
        if (result.state == 1 && result.axiosError.response?.status == 401) {
            navigate('/logout')
            return
        }
        if (result.state == 0) {
            const list = result.data.list || []
            setRantingList(list.map((v: any) => ({ year: "" + v.year, position: +v.position, active: v.active, ranting: +v.ranting_id })))
        }
    }

    useEffect(() => {
        let username = globalContext?.GetLoginData().username || ""
        if (username === "") {
            navigate('/login')
            return
        }
        const pesilatID = +(params.userid || 0)
        if (pesilatID > 0) {
            loadPesilat(pesilatID)
            loadPrestasi(pesilatID, 1)
            loadPrestasi(pesilatID, 2)
            loadRantingList(pesilatID)
        } else {
            setLoading(false)
        }
    }, [])

    const findLabel = (list: { value: number, label: string }[], value: number) => {
        const found = list.find(item => item.value === value)
        return found ? found.label : "-"
    }

    const InfoRow = ({ label, value }: { label: string, value: React.ReactNode }) => (
        <HStack spacing={2} align="start" justify="start">
            <Text fontSize='sm' minW={'140px'} color={'gray.500'}>{label}</Text>
            <Text fontSize='sm' fontWeight={'bold'}>{value}</Text>
        </HStack>
    )

    return (
        <Box py={"10px"} px={"10px"} maxW='m' borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />
            <ConfirmModal isOpen={confirmDelete} title={t('confirm_requested')} mainText={t('confirm_delete') + ' ' + (pesilat?.nama || '') + '?'}
                confirmButtonText={t('Delete')} closeButtonText={t('Cancel')}
                closeFunction={() => setConfirmDelete(false)} confirmFunction={deleteUser} />
            <RantingInfoModal isOpen={infoRantingModalOpen} closeFunction={() => setInfoRantingModalOpen(false)} rantingId={rantingToView} />

            {loading ? (
                <Center py={"40px"}>
                    <Spinner size='lg' />
                </Center>
            ) : pesilat ? (
                <SimpleGrid py={"10px"} px={"10px"} borderWidth='2px' borderRadius='lg' overflow='auto' >
                    <SimpleGrid columns={[1]} maxW={['340px', '700px']} minW={['340px', '700px']} justifySelf={'center'}>

                        <Center mb={"15px"}>
                            {pesilat.file1 && pesilat.file1 !== "" ? (
                                <Box h="160px" w="130px" bg={"gray.100"} overflow={'hidden'}>
                                    <Image
                                        src={Const.File_Download + pesilat.file1}
                                        width={130}
                                        height={160}
                                        objectFit={'cover'}
                                        fallbackSrc=''
                                    />
                                </Box>
                            ) : (
                                <Box h="160px" w="130px" bg={"gray.100"} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                                    <Text fontSize='xs' color={'gray.400'}>{t('no_profile_picture')}</Text>
                                </Box>
                            )}
                        </Center>

                        <VStack spacing={2} align="start">
                            <InfoRow label={t('Username')} value={pesilat.username} />
                            <InfoRow label={t('Full Name')} value={pesilat.nama} />
                            <InfoRow label={t('Country')} value={countryName || pesilat.country_id} />
                            <InfoRow label={t('Sex')} value={pesilat.kelamin === 'm' ? t('Male') : pesilat.kelamin === 'f' ? t('Female') : '-'} />
                            <InfoRow label={t('Education')} value={findLabel(pendidikanList, pesilat.pendidikan)} />
                            <InfoRow label={t('Belt')} value={findLabel(sabukList, pesilat.sabuk)} />
                            <InfoRow label={t('joined_kpsn')} value={pesilat.tahun_masuk > 0 ? pesilat.tahun_masuk : '-'} />
                        </VStack>

                        <Box mt={"15px"} mb={"5px"}>
                            <Text fontSize='sm' color={'gray.500'} mb={"5px"}>{t('Professions')}</Text>
                            <HStack spacing={2} wrap={'wrap'}>
                                {pesilat.athlete ? (
                                    <Badge colorScheme='green'>{t('Athlete')}</Badge>
                                ) : ''}
                                {pesilat.coach ? (
                                    <Badge colorScheme='green'>{t('Coach')}</Badge>
                                ) : ''}
                                {pesilat.referee ? (
                                    <Badge colorScheme='green'>{t('Referee')}</Badge>
                                ) : ''}
                                {pesilat.other_profession && pesilat.other_profession !== "" ? (
                                    <Badge colorScheme='green'>{pesilat.other_profession}</Badge>
                                ) : ''}
                                {!pesilat.athlete && !pesilat.coach && !pesilat.referee && (!pesilat.other_profession || pesilat.other_profession === "") ? (
                                    <Text fontSize='sm' color={'gray.400'}>-</Text>
                                ) : ''}
                            </HStack>
                        </Box>

                        {pesilat.coach && pesilat.coach_level > 0 ? (
                            <InfoRow label={t('Coach Level')} value={findLabel(levelList, pesilat.coach_level)} />
                        ) : ''}

                        {pesilat.referee ? (
                            <VStack spacing={2} align="start" mt={"5px"}>
                                {pesilat.referee_level > 0 ? (
                                    <InfoRow label={t('Referee Level')} value={findLabel(levelList, pesilat.referee_level)} />
                                ) : ''}
                                {pesilat.referee_first_upgrade > 0 ? (
                                    <InfoRow label={t('First Referee Upgrade')} value={pesilat.referee_first_upgrade} />
                                ) : ''}
                                {pesilat.referee_last_upgrade > 0 ? (
                                    <InfoRow label={t('Recent Referee Upgrade')} value={pesilat.referee_last_upgrade} />
                                ) : ''}
                            </VStack>
                        ) : ''}

                        {athletePrestasi.length > 0 ? (
                            <Box mt={"15px"}>
                                <Text fontSize='sm' color={'gray.500'} mb={"5px"}>{t('athlete_accomplishment')}</Text>
                                <TableContainer>
                                    <Table variant='simple' colorScheme='blue' size='sm'>
                                        <Thead>
                                            <Tr>
                                                <Th>{t('Year')}</Th>
                                                <Th>{t('Level')}</Th>
                                                <Th>{t('Event Name')}</Th>
                                                <Th>{t('Medal')}</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {athletePrestasi.map((p, i) => (
                                                <Tr key={i}>
                                                    <Td fontSize='xs'>{p.year}</Td>
                                                    <Td fontSize='xs'>{prestasiLevelList[p.level] || '-'}</Td>
                                                    <Td fontSize='xs'><Tooltip label={p.event}>{fixStringLength(p.event, 30)}</Tooltip></Td>
                                                    <Td fontSize='xs'>{medalList[p.medal] || '-'}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : ''}

                        {coachExp.length > 0 ? (
                            <Box mt={"15px"}>
                                <Text fontSize='sm' color={'gray.500'} mb={"5px"}>{t('add_coach_exp')}</Text>
                                <TableContainer>
                                    <Table variant='simple' colorScheme='blue' size='sm'>
                                        <Thead>
                                            <Tr>
                                                <Th>{t('Year')}</Th>
                                                <Th>{t('TimCabangRanting')}</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {coachExp.map((p, i) => (
                                                <Tr key={i}>
                                                    <Td fontSize='xs'>{p.year}</Td>
                                                    <Td fontSize='xs'><Tooltip label={p.team}>{fixStringLength(p.team, 40)}</Tooltip></Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : ''}

                        {rantingList.length > 0 ? (
                            <Box mt={"15px"}>
                                <Text fontSize='sm' color={'gray.500'} mb={"5px"}>{t('list_ranting')}</Text>
                                <TableContainer>
                                    <Table variant='simple' colorScheme='blue' size='sm'>
                                        <Thead>
                                            <Tr>
                                                <Th>{t('Year')}</Th>
                                                <Th>{t('As')}</Th>
                                                <Th>{t('active')}</Th>
                                                <Th>{t('Ranting')}</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {rantingList.map((r, i) => (
                                                <Tr key={i}>
                                                    <Td fontSize='xs'>{r.year}</Td>
                                                    <Td fontSize='xs'>{posisiList[r.position] || '-'}</Td>
                                                    <Td fontSize='xs'>
                                                        {r.active ? (
                                                            <Icon as={AiOutlineCheckCircle} color='green.400' w={4} h={4} />
                                                        ) : (
                                                            <Icon as={MdOutlineDoNotDisturbAlt} color='gray.400' w={4} h={4} />
                                                        )}
                                                    </Td>
                                                    <Td fontSize='xs'>
                                                        <Button size='xs' onClick={() => { setRantingToView(r.ranting); setInfoRantingModalOpen(true) }}>{r.ranting}</Button>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : ''}

                        {isAdmin ? (
                            <Center mt={"20px"}>
                                <HStack spacing={3}>
                                    <Button size='sm' colorScheme='blue' onClick={() => navigate('/profile/' + pesilat.id)}>
                                        {t('Edit User')}
                                    </Button>
                                    <Button size='sm' colorScheme='red' isLoading={deleting} onClick={() => setConfirmDelete(true)}>
                                        {t('Delete User')}
                                    </Button>
                                </HStack>
                            </Center>
                        ) : ''}

                    </SimpleGrid>
                </SimpleGrid>
            ) : (
                <Center py={"40px"}>
                    <Text fontSize='sm'>{t('no_records')}</Text>
                </Center>
            )}
        </Box>
    )
}
